import * as React from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Label,
  LabelList,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useIntl } from "react-intl";
import { Spinner, useMediaQuery, useTheme } from "@wayflyer/flyui";
import type { ReferencedChartProps, ValueType } from "@wayflyer/flyui-dataviz";
import { barChartLabel, refChartLabel } from "@wayflyer/flyui-dataviz";

import { ltvChartTooltip, XAxisTick } from "./helpers";

/**
 * LtvCac chart component
 */

export interface LtvCacChartProps extends ReferencedChartProps {
  currentValueName?: string;
  referenceValueName?: string;
  isCustomer?: boolean;
  separateYAxis?: boolean;
}

export const LtvCac = ({
  data,
  formatValue,
  width = 0, // 0 leaves height determine width, >0 is just used for snapshots to ensure chart is rendered
  isCustomer = false,
  loading = false,
  currentValueName = "",
  referenceValueName = "",
  separateYAxis = false,
}: Optional<LtvCacChartProps, "formatValue">): UIElement => {
  const query = useMediaQuery();
  const intl = useIntl();
  const mediumWidth = query("sm").windowGt;
  const theme = useTheme();

  const tickFormatter = (value: ValueType): string => {
    return formatValue ? (formatValue(value, false) as string) : value.toString();
  };

  const tickFormatterRef = (value: ValueType): string => {
    return formatValue ? (formatValue(value, separateYAxis) as string) : value.toString();
  };

  if (loading) {
    return <Spinner variant="monochrome" />;
  }

  const referenceValueColor = theme.palette.dataviz.categorical.category1;
  const referenceValueColorPaler = theme.palette.dataviz.sequential.caelum[300];
  const currentValueColor = theme.palette.dataviz.categorical.category4;
  const customerColor = theme.palette.content.neutral.subtle;
  const chartWidth = width
    ? {
        width,
      }
    : {};

  return data ? (
    <ResponsiveContainer height={mediumWidth ? 500 : 100} {...chartWidth}>
      <ComposedChart
        data={data}
        height={mediumWidth ? 400 : 80}
        margin={{
          top: 20,
          right: 30,
          left: 20,
          bottom: 10,
        }}
      >
        {mediumWidth ? (
          <CartesianGrid strokeWidth="0.5" stroke={theme.palette.bg.neutral.strong} {...({ yAxisId: "left" } as any)} />
        ) : null}
        {mediumWidth ? <XAxis interval={0} tick={<XAxisTick />} dataKey="day" height={100} /> : null}
        {mediumWidth ? (
          <YAxis tick={{ fontSize: 12 }} tickFormatter={tickFormatterRef} yAxisId="left">
            {separateYAxis ? (
              <Label angle={270} position="left" style={{ textAnchor: "middle" }}>
                {referenceValueName}
              </Label>
            ) : null}
          </YAxis>
        ) : null}
        {mediumWidth ? (
          <YAxis tick={{ fontSize: 12 }} tickFormatter={tickFormatter} yAxisId="right" orientation="right">
            {separateYAxis ? (
              <Label angle={270} position="right" style={{ textAnchor: "middle" }}>
                {currentValueName}
              </Label>
            ) : null}
          </YAxis>
        ) : null}
        {mediumWidth ? <Tooltip content={ltvChartTooltip(formatValue)} /> : null}

        <Bar
          name={referenceValueName}
          type="monotone"
          dataKey="referenceValue"
          fillOpacity="0.6"
          isAnimationActive={false}
          stackId={1}
          yAxisId="left"
          fill={isCustomer ? customerColor : referenceValueColor}
        >
          {mediumWidth ? (
            <LabelList
              dataKey="referenceValue"
              position="top"
              stroke={theme.palette.content.neutral.strong}
              content={refChartLabel(formatValue) as any}
            />
          ) : null}
        </Bar>
        {!isCustomer ? (
          <Bar
            name={`${referenceValueName} (${intl.formatMessage({
              id: "LTV - incomplete",
              defaultMessage: "incomplete",
            })})`}
            type="monotone"
            dataKey="referenceContinuationValue"
            fillOpacity="0.6"
            isAnimationActive={false}
            stackId={1}
            yAxisId="left"
            fill={referenceValueColorPaler}
          >
            {mediumWidth ? (
              <LabelList
                dataKey="referenceContinuationValue"
                position="top"
                content={refChartLabel(formatValue) as any}
                stroke={theme.palette.content.neutral.strong}
              />
            ) : null}
          </Bar>
        ) : null}

        <Line
          name={currentValueName}
          type="monotone"
          dataKey="currentValue"
          strokeWidth="1"
          stroke={currentValueColor}
          dot={mediumWidth ? true : false}
          activeDot={true}
          isAnimationActive={false}
          yAxisId={separateYAxis ? "right" : "left"}
        >
          {mediumWidth ? (
            <LabelList stroke={theme.palette.content.neutral.subtle} content={barChartLabel(formatValue) as any} />
          ) : null}
        </Line>
        <Legend />
      </ComposedChart>
    </ResponsiveContainer>
  ) : null;
};
